import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, type ApiResponse } from '@/lib/api-client';

/**
 * Staff-controlled per-client notification categories. Each maps to a
 * notification preference category on the API. `disabled=true` means the
 * client will NOT receive notifications in that category.
 */
export const NOTIFICATION_GATE_CATEGORIES = [
  {
    key: 'document_requests',
    label: 'Document requests',
    description: 'Requests for documents and uploads',
  },
  {
    key: 'job_status_updates',
    label: 'Jobs & tasks',
    description: 'Job status changes and assigned tasks',
  },
  {
    key: 'invoice_notifications',
    label: 'Invoices & billing',
    description: 'Invoices, quotes, and payment reminders',
  },
  {
    key: 'general_messages',
    label: 'Messages',
    description: 'Messages and general communications',
  },
] as const;

export type NotificationGateCategory = (typeof NOTIFICATION_GATE_CATEGORIES)[number]['key'];

/** category -> disabled map (categories not present are delivered). */
export type ContactNotificationGates = Record<string, boolean>;

export const contactGateKeys = {
  all: ['contact-notification-gates'] as const,
  detail: (contactId: string) => [...contactGateKeys.all, contactId] as const,
  list: () => [...contactGateKeys.all, 'list'] as const,
};

/** A client row for the standalone notification-settings matrix. */
export interface ClientNotificationGates {
  contactId: string;
  name: string;
  email: string | null;
  gates: ContactNotificationGates;
}

/**
 * Fetch the logged-in user's associated clients with their notification gates
 * (for the standalone Notification Settings matrix page). Scoped server-side to
 * the caller's assigned/team clients.
 */
export function useClientNotificationGatesList(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: contactGateKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<ClientNotificationGates[]>>(
        '/contacts/notification-gates',
      );
      return response.data.data ?? [];
    },
    enabled: options?.enabled !== false,
  });
}

/** Fetch the staff notification gates for a contact. */
export function useContactNotificationGates(contactId: string) {
  return useQuery({
    queryKey: contactGateKeys.detail(contactId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<ContactNotificationGates>>(
        `/contacts/${contactId}/notification-gates`,
      );
      return response.data.data ?? {};
    },
    enabled: !!contactId,
  });
}

/** Upsert staff notification gates for a contact. */
export function useUpdateContactNotificationGates(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (gates: ContactNotificationGates) => {
      const response = await apiClient.put<ApiResponse<ContactNotificationGates>>(
        `/contacts/${contactId}/notification-gates`,
        { gates },
      );
      return response.data.data ?? {};
    },
    onSuccess: (data) => {
      queryClient.setQueryData(contactGateKeys.detail(contactId), data);
    },
  });
}
